<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import PowerAppMfaChallenge from './PowerAppMfaChallenge.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const src = ref('');
const error = ref(false);
// Set when the control plane answers 206: the app is flagged requires_mfa and
// wants a second factor before it mints anything.
const needsMfa = ref(false);
const mfaError = ref('');
const isVerifying = ref(false);

// O que TODO PowerApp recebe. Fica aqui, e não no registro, porque é igual para
// todos: no banco, alguém removeria uma peça e derrubaria a frota inteira. O
// extra de cada app (allow-downloads, allow-modals) vem de
// `account_apps.frame_capabilities` e chega junto com o token.
const SANDBOX_PADRAO = 'allow-scripts allow-forms allow-same-origin allow-popups';
const sandbox = ref(SANDBOX_PADRAO);

// The SSO token is handed to the embedded app via postMessage (never in the
// URL, which would leak into history / server logs / Referer) and is never held
// here between messages — every handoff mints a fresh one.
let appOrigin = '';
// A re-mint that stopped on the MFA challenge, waiting for the code. Held so
// the token lands in the iframe that asked for it once the user answers.
let pendingRefresh = null;

// Mints a token for the app currently embedded. `slug` is captured by the
// caller so a reply that lands after the user switched apps is dropped instead
// of handing app B a token minted for app A. POST, because `credentials` may
// carry the MFA code and a code does not belong in a query string.
const mintToken = async (slug, credentials = {}) => {
  const { accountId } = route.params;
  const { data, status } = await window.axios.post(
    `/api/v1/accounts/${accountId}/powerapps/${slug}/token`,
    credentials,
    // 206 is a challenge, not a failure: let it through to be handled below.
    { validateStatus: s => (s >= 200 && s < 300) || s === 206 }
  );
  return { data, status };
};

// A user with no second factor configured cannot get a token for a sensitive
// app at all, so send them where they can set one up.
const goToMfaSetup = () => {
  router.push({ name: 'profile_settings_mfa' });
};

const handleMintError = err => {
  if (err.response?.status === 403 && err.response.data?.mfa_setup_required) {
    goToMfaSetup();
    return;
  }
  if (err.response?.status === 401 && needsMfa.value) {
    mfaError.value = t('POWER_APPS.MFA.INVALID_CODE');
    return;
  }
  error.value = true;
};

// The embedded app asks again whenever a request comes back 401 — a recording
// runs far longer than the token lives. Re-mint here: replaying the token we
// handed out on load is replaying exactly the one that just expired, so the
// app's retry 401s too and a long recording dies on upload. This is what the
// mobile shell already does (PowerAppScreen.tsx).
const onMessage = async e => {
  if (!appOrigin || e.origin !== appOrigin) return;
  if (e.data?.type !== 'powerbizz-app-ready' || !e.source) return;
  const { slug } = route.params;
  const origin = appOrigin;
  try {
    const { data, status } = await mintToken(slug);
    // The iframe may have been swapped while the request was in flight.
    if (appOrigin !== origin || route.params.slug !== slug) return;
    if (status === 206) {
      // The tolerance window closed mid-session. Ask for the code over the
      // still-mounted iframe so the app keeps its state.
      pendingRefresh = { source: e.source, origin, slug };
      needsMfa.value = true;
      return;
    }
    e.source.postMessage({ type: 'powerbizz-sso', token: data.token }, origin);
  } catch {
    // App keeps the expired token and shows its own session-expired message.
  }
};

const loadApp = async () => {
  // Reset first so the iframe unmounts (forces a clean re-embed instead of
  // reusing the previous app's stale src) while the new token is fetched.
  src.value = '';
  error.value = false;
  needsMfa.value = false;
  mfaError.value = '';
  pendingRefresh = null;
  appOrigin = '';
  sandbox.value = SANDBOX_PADRAO;
  const { slug } = route.params;
  try {
    const { data, status } = await mintToken(slug);
    if (status === 206) {
      // No token was minted, so nothing is embedded yet: ask for the code
      // first. The iframe only mounts once src is set.
      needsMfa.value = true;
      return;
    }
    appOrigin = new URL(data.url, window.location.origin).origin;
    // Antes do src, sempre: é o src que monta o iframe, e as flags de sandbox
    // valem a partir da navegação. Depois dele, a moldura já existiria com a
    // permissão errada até a próxima carga.
    sandbox.value = data.sandbox || SANDBOX_PADRAO;
    src.value = data.url;
  } catch (err) {
    handleMintError(err);
  }
};

// Answers the challenge, then resumes whatever was interrupted: handing the
// token to the iframe that asked for it, or embedding the app for the first
// time.
const onMfaSubmit = async credentials => {
  const { slug } = route.params;
  const refresh = pendingRefresh;
  isVerifying.value = true;
  mfaError.value = '';
  try {
    const { data } = await mintToken(slug, credentials);
    needsMfa.value = false;
    pendingRefresh = null;
    if (refresh) {
      if (route.params.slug !== refresh.slug) return;
      refresh.source.postMessage(
        { type: 'powerbizz-sso', token: data.token },
        refresh.origin
      );
      return;
    }
    appOrigin = new URL(data.url, window.location.origin).origin;
    src.value = data.url;
  } catch (err) {
    handleMintError(err);
  } finally {
    isVerifying.value = false;
  }
};

onMounted(() => {
  window.addEventListener('message', onMessage);
  loadApp();
});

// Vue Router reuses this component instance when only the route params change
// (e.g. navigating from /apps/expclub to /apps/powercoach) — without this
// watcher the iframe would keep showing whichever app was opened first.
watch(() => [route.params.accountId, route.params.slug], loadApp);

onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<template>
  <div class="relative flex flex-col w-full h-full bg-n-background">
    <iframe
      v-if="src"
      :src="src"
      class="flex-1 w-full border-0"
      :sandbox="sandbox"
      :title="$t('POWER_APPS.TITLE')"
    />
    <!-- Over the iframe, not instead of it: a challenge raised by a re-mint
         must not unmount the app and throw away what the user was doing. -->
    <div
      v-if="needsMfa"
      class="absolute inset-0 z-10 flex bg-n-background/95"
    >
      <PowerAppMfaChallenge
        :error-message="mfaError"
        :is-verifying="isVerifying"
        @submit="onMfaSubmit"
      />
    </div>
    <div
      v-else-if="!src"
      class="grid flex-1 text-sm place-content-center text-n-slate-11"
    >
      <span v-if="error">{{ $t('POWER_APPS.OPEN_ERROR') }}</span>
      <span v-else>{{ $t('POWER_APPS.LOADING') }}</span>
    </div>
  </div>
</template>
